import type { BadgeTone } from '../../../shared/ui/Badge';
import type {
  SupportSettings, SupportTicket, SupportTicketStatus, SupportPriority,
} from './types';

export const DEFAULT_SUPPORT_SETTINGS: SupportSettings = {
  enabled: true,
  categories: ['Bug', 'Dúvida', 'Sugestão', 'Financeiro', 'Outro'],
  priorities: [
    { key: 'low', label: 'Baixa', targetHours: null },
    { key: 'normal', label: 'Média', targetHours: 48 },
    { key: 'high', label: 'Alta', targetHours: 8 },
  ],
  welcomeMessage:
    'Descreva o problema com o máximo de detalhe (passo a passo) e anexe um print se possível.',
};

export function resolveSupportSettings(
  raw: Partial<SupportSettings> | undefined | null,
): SupportSettings {
  if (!raw) return DEFAULT_SUPPORT_SETTINGS;
  return {
    enabled: raw.enabled ?? DEFAULT_SUPPORT_SETTINGS.enabled,
    categories: raw.categories ?? DEFAULT_SUPPORT_SETTINGS.categories,
    priorities: raw.priorities ?? DEFAULT_SUPPORT_SETTINGS.priorities,
    welcomeMessage: raw.welcomeMessage ?? DEFAULT_SUPPORT_SETTINGS.welcomeMessage,
  };
}

export const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  open: 'Aberto', in_progress: 'Em andamento', resolved: 'Resolvido', closed: 'Fechado',
};
export const PRIORITY_LABELS: Record<SupportPriority, string> = {
  low: 'Baixa', normal: 'Média', high: 'Alta',
};

/** Tom do Badge por status (cor coerente com o resto do app). */
export const STATUS_TONE: Record<SupportTicketStatus, BadgeTone> = {
  open: 'info', in_progress: 'warning', resolved: 'success', closed: 'neutral',
};
/** Tom do Badge por prioridade. */
export const PRIORITY_TONE: Record<SupportPriority, BadgeTone> = {
  low: 'neutral', normal: 'info', high: 'error',
};

/** Status "abertos" (em aberto) — únicos que contam pro badge. Resolvido/fechado
 *  não inflam o contador mesmo se ainda houver flag de não-lido. */
const OPEN_STATUSES: ReadonlySet<SupportTicketStatus> = new Set(['open', 'in_progress']);

/** Regra única de "não lido para mim" na clínica: o autor vê o próprio; o gerente vê
 *  também os abertos pelo Ardix. `unreadForAdmins` é do Ardix e a clínica não o lê. */
export function isUnreadForMe(t: SupportTicket, uid: string, isManager: boolean): boolean {
  return t.unreadForOwner && (t.createdBy === uid || (!!t.openedByArdix && isManager));
}

/** Chamado que a clínica tirou da lista (o Ardix continua vendo). */
export function isRemoved(t: Pick<SupportTicket, 'removedAt'>): boolean {
  return t.removedAt != null;
}

/** Badge total: conta os chamados abertos (open/in_progress) não lidos para mim. */
export function badgeForTickets(
  tickets: SupportTicket[], uid: string, isManager: boolean,
): number {
  let n = 0;
  for (const t of tickets) {
    if (isRemoved(t) || !OPEN_STATUSES.has(t.status)) continue;
    if (isUnreadForMe(t, uid, isManager)) n++;
  }
  return n;
}

/** Quem escreveu por último, para a linha da lista (o Ardix assina como "Suporte Ardix"). */
export function lastAuthorLabel(t: Pick<SupportTicket, 'lastMessageBy'>): string | null {
  if (t.lastMessageBy === 'admin') return 'Suporte Ardix';
  if (t.lastMessageBy === 'owner') return 'Clínica';
  return null;
}

/** Destaque que fica mesmo depois de lido: o Ardix respondeu e a bola está com a clínica. */
export function ardixReplied(t: Pick<SupportTicket, 'lastMessageBy' | 'status'>): boolean {
  return t.lastMessageBy === 'admin' && (t.status === 'open' || t.status === 'in_progress');
}
